/**
 * Publications Page - ESS Design System
 *
 * A page displaying scientific publications with filtering capabilities:
 * - PageHeaderBackground component for the hero section
 * - Filtering form with Year and Publication Type filters
 * - Results listing with publication details fetched from Plone API
 * - Server-side pagination and filtering via searchContent
 * - Responsive layout (row in desktop, column in mobile)
 *
 * Content types: written_publication, oral_publication
 *
 * Requires: publication_date DateIndex in catalog (upgrade step v1004).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL, Helmet } from '@plone/volto/helpers';
import { PageHeaderBackground } from '../../Molecules/PageHeaderBackground';
import Pagination from '../../Molecules/Pagination/Pagination';
import { ESSButton } from '../../Atoms/ESSButton';
import Dropdown from '../../Molecules/DropDown/Dropdown';
import { RichText } from 'volto-essvoltocomponents/components/Atoms';
import { buildCitation } from '../PublicationItemView/publicationUtils';
import {
  ContentWide,
  ContentWrapper,
  PageSection,
} from 'volto-essvoltocomponents/components/Layout';
import './PublicationsView.scss';

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------

const messages = defineMessages({
  pageTitle: {
    id: 'publications.title',
    defaultMessage: 'Publications',
  },
  pageDescription: {
    id: 'publications.description',
    defaultMessage:
      'Publications of the members of the Earth System Services group in scientific journals, book chapters, oral presentations and posters in scientific conferences',
  },
  yearFilter: {
    id: 'publications.filter.year',
    defaultMessage: 'Year',
  },
  publicationTypeFilter: {
    id: 'publications.filter.type',
    defaultMessage: 'Publication type',
  },
  viewAll: {
    id: 'publications.filter.viewAll',
    defaultMessage: 'View all',
  },
  applyFilters: {
    id: 'publications.filter.apply',
    defaultMessage: 'APPLY',
  },
  resultsTitle: {
    id: 'publications.results',
    defaultMessage: 'Results',
  },
  noResults: {
    id: 'publications.noResults',
    defaultMessage: 'Sorry there were no results for your search.',
  },
  filterBy: {
    id: 'publications.filter.filterBy',
    defaultMessage: 'Filter by:',
  },
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 10;

const PORTAL_TYPES = ['written_publication', 'oral_publication'];

const METADATA_FIELDS = [
  'publication_authors',
  'publication_date',
  'publication_type',
  'volume_name',
  'volume_pages',
  'doi',
  'url',
  'conference_title',
  'conference_location',
];

const EMPTY_ARRAY = [];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the year string from a publication_date value.
 * Handles both "YYYY-MM-DD" strings and ISO datetime strings.
 */
const getYear = (dateStr) => {
  if (!dateStr) return '';
  return String(dateStr).substring(0, 4);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PublicationsView = ({ content: contentProp }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Use prop when available (contentTypesView), fall back to Redux (route)
  const reduxContent = useSelector((state) => state.content?.data);
  const content = contentProp || reduxContent;
  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : '';

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedType, setSelectedType] = useState('');
  // Applied filters (only update on APPLY click)
  const [appliedYear, setAppliedYear] = useState('');
  const [appliedType, setAppliedType] = useState('');

  // --- 1) Lightweight fetch to derive filter options (years + types) ---
  const filterOptionsKey = `publicationsFilterOptions-${contentPath}`;

  const filterOptionsLoaded = useSelector(
    (state) =>
      state.search.subrequests?.[filterOptionsKey]?.loaded || false,
  );

  useEffect(() => {
    if (!contentPath || filterOptionsLoaded) return;
    dispatch(
      searchContent(
        contentPath,
        {
          portal_type: PORTAL_TYPES,
          review_state: 'published',
          b_size: 999,
          metadata_fields: ['publication_date', 'publication_type'],
        },
        filterOptionsKey,
      ),
    );
  }, [dispatch, contentPath, filterOptionsKey, filterOptionsLoaded]);

  const allPublished = useSelector(
    (state) =>
      state.search.subrequests?.[filterOptionsKey]?.items || EMPTY_ARRAY,
  );

  const yearOptions = useMemo(() => {
    const years = [
      ...new Set(
        allPublished
          .map((pub) => getYear(pub.publication_date))
          .filter(Boolean),
      ),
    ].sort((a, b) => b.localeCompare(a));
    return [
      { value: '', label: 'View all' },
      ...years.map((y) => ({ value: y, label: y })),
    ];
  }, [allPublished]);

  const typeOptions = useMemo(() => {
    const types = [
      ...new Set(
        allPublished
          .map((pub) => pub.publication_type)
          .filter(Boolean),
      ),
    ].sort((a, b) => a.localeCompare(b));
    return [
      { value: '', label: 'View all' },
      ...types.map((y) => ({ value: y, label: y })),
    ];
  }, [allPublished]);

  // --- 2) Main listing: server-side paginated + filtered ---
  const listingKey = `publicationsListing-${contentPath}-${currentPage}-${appliedYear}-${appliedType}`;

  const listingLoaded = useSelector(
    (state) => state.search.subrequests?.[listingKey]?.loaded || false,
  );

  useEffect(() => {
    if (!contentPath || listingLoaded) return;
    const query = {
      portal_type: PORTAL_TYPES,
      sort_on: 'publication_date',
      sort_order: 'descending',
      review_state: 'published',
      metadata_fields: METADATA_FIELDS,
      b_size: PAGE_SIZE,
      b_start: (currentPage - 1) * PAGE_SIZE,
    };

    if (appliedType) {
      query.publication_type = appliedType;
    }

    if (appliedYear) {
      query['publication_date.query:list:date'] = [
        `${appliedYear}-01-01`,
        `${appliedYear}-12-31`,
      ];
      query['publication_date.range'] = 'min:max';
    }

    dispatch(searchContent(contentPath, query, listingKey));
  }, [dispatch, contentPath, currentPage, appliedYear, appliedType, listingKey, listingLoaded]);

  const publications = useSelector(
    (state) =>
      state.search.subrequests?.[listingKey]?.items || EMPTY_ARRAY,
  );
  const totalItems = useSelector(
    (state) => state.search.subrequests?.[listingKey]?.total || 0,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.[listingKey]?.loaded || false,
  );

  const loading = useSelector(
    (state) => state.search.subrequests?.[listingKey]?.loading || false,
  );
  
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // --- Filter handlers ---
  const handleApplyFilters = () => {
    setAppliedYear(selectedYear);
    setAppliedType(selectedType);
    setCurrentPage(1);
  };

  // ── Hero image — uses hero_image from the multimedia content type schema ──
  const heroImageUrl = content?.hero_image?.download
    ? flattenToAppURL(content.hero_image.download)
    : null;


  return (
    <div className="publications-page">
      <Helmet title={intl.formatMessage(messages.pageTitle)} />

      {/* Header Section */}
      <PageHeaderBackground
        title={intl.formatMessage(messages.pageTitle)}
        description={intl.formatMessage(messages.pageDescription)}
        backgroundImage={heroImageUrl}
        overlayColor="rgba(255, 255, 255, 1)"
        overlayOpacity={0.8}
      />

      {/* Main Content */}
      <PageSection 
        paddingBottom="var(--ess-space-7xl)"
        className="publications-content">
          <ContentWrapper           
            className="publications-content__listing-section"
            minHeight="55vh">
          <ContentWide>
            <div className="publications-layout">
              {/* Filters Section */}
              <aside className="publications-filters">
                <h4 className="publications-filters__title">
                  {intl.formatMessage(messages.filterBy)}
                </h4>

                <div className="publications-filters__group">
                  <label className="publications-filters__label">
                    {intl.formatMessage(messages.yearFilter)}
                  </label>
                  {/* Stateless dropdown, only renders based on props and calls a callback owned by the parent component */}
                  <Dropdown
                    id="year-filter"
                    label={yearOptions.find((item) => item.value === selectedYear)?.label}
                    items={yearOptions.map((item) => ({
                      id: item.value,
                      label: item.label,
                      onSelect: () => setSelectedYear(item.value),
                    }))}
                    className="who-we-are-view__dropdown"
                  />
                </div>

                <div className="publications-filters__group">
                  <label className="publications-filters__label">
                    {intl.formatMessage(messages.publicationTypeFilter)}
                  </label>
                  {/* Stateless dropdown, only renders based on props and calls a callback owned by the parent component */}
                  <Dropdown
                    id="type-filter"
                    label={typeOptions.find((item) => item.value === selectedType)?.label}
                    items={typeOptions.map((item) => ({
                      id: item.value,
                      label: item.label,
                      onSelect: () => setSelectedType(item.value),
                    }))}
                    className="who-we-are-view__dropdown"
                  />
                </div>
                <div className="publications-filters__actions">
                  <ESSButton
                    variant="positive"
                    onClick={handleApplyFilters}
                    isFullWidth
                  >
                    {intl.formatMessage(messages.applyFilters)}
                  </ESSButton>
                </div>
              </aside>

              {/* Results Section */}
              <main className="publications-results">
                <h2>{intl.formatMessage(messages.resultsTitle)}</h2>

                {loaded && publications.length === 0 ? (
                  <p>{intl.formatMessage(messages.noResults)}</p>
                ) : (
                  <div className="publications-list">
                    {publications.map((pub) => {
                      const citation = buildCitation(pub);
                      return (
                        <article key={pub['@id']} className="publication-item">
                          <RichText
                            content={citation}
                            className="publication-citation"
                          />
                        </article>
                      );
                    })}
                  </div>
                )}

                {/* Loading */}
                {loading && <p className="publications-list__loading">Loading…</p>}
                
                {!loading && totalPages > 1 && (
                  <ContentWide 
                    className="publications-pagination" 
                    alignment="center"
                    mobileAlignment='center'
                    paddingTop="var(--spacing-4)">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => {
                        setCurrentPage(page);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </ContentWide>
                )}
              </main>
            </div>
          </ContentWide>
        </ContentWrapper>
      </PageSection>
    </div>
  );
};

export default PublicationsView;
